import { isValidQuantityPackApiName } from './typeguards';
import { VALID_QUANTITY_NAMES } from './validTypeValues';

/**
 * Normalizes query parameters to ensure consistent array format
 */
export const normalizeQueryParams = (products, packs) => {
  const stringProductConfigs = products ? typeof products === 'string' ? [products] : products : [];
  const stringQuantityPacks = packs ? typeof packs === 'string' ? [packs] : packs : [];
  return {
    stringProductConfigs,
    stringQuantityPacks
  };
};

/**
 * Validates and normalizes quantity values from query parameters
 */
export const validateQuantity = quantityString => {
  if (!quantityString) return 1;
  const quantity = Math.abs(Number.parseInt(quantityString, 10));
  return Number.isInteger(quantity) ? quantity : 1;
};

/**
 * Parses a product configuration string and extracts components
 */
export const parseProductEntry = entry => {
  const split = entry.toString().split('_');
  return {
    productApiName: split[0],
    quantityString: split[1],
    isIncremental: split[2] === 'inc',
    hasValidFormat: split.length > 0
  };
};

/**
 * Parses a quantity pack configuration string and extracts components
 * Supports both formats:
 * - Old format: quantityPackApiName_productApiName_quantity[_inc]
 * - New format: quantityName_productApiName_quantity[_inc]
 *
 * Handles quantity names that contain underscores (e.g., SALES_SEAT) by checking
 * if the entry starts with any valid quantity name.
 */
export const parseQuantityPackEntry = entry => {
  const entryString = entry.toString();
  const split = entryString.split('_');
  let quantityName;
  let quantityPackApiName;
  let remainingParts = split;

  // Check if entry starts with any valid quantity name (e.g., SALES_SEAT, CONTACTS)
  // This handles names with underscores by checking the full name
  for (const validQuantityName of VALID_QUANTITY_NAMES) {
    if (entryString.startsWith(`${validQuantityName}_`)) {
      quantityName = validQuantityName;
      remainingParts = entryString.slice(validQuantityName.length + 1).split('_');
      break;
    }
  }

  // If no quantity name found, assume old format with quantity pack API name
  // (e.g., additional-marketing-contacts). These use hyphens, not underscores,
  // so the simple split works fine
  if (!quantityName) {
    const firstPart = split[0];
    if (isValidQuantityPackApiName(firstPart)) {
      quantityPackApiName = firstPart;
      remainingParts = split.slice(1);
    }
  }
  return {
    quantityPackApiName,
    quantityName,
    parentProductApiName: remainingParts[0],
    quantityString: remainingParts[1],
    isIncremental: remainingParts[2] === 'inc',
    hasValidFormat: !!(quantityName || quantityPackApiName) && remainingParts.length >= 2
  };
};

/**
 * Calculates the final quantity for a product, handling incremental additions
 */
export const calculateProductQuantity = (baseQuantity, isIncremental, ownedQuantity) => {
  if (isIncremental) {
    return ownedQuantity + baseQuantity;
  }
  return baseQuantity;
};

/**
 * Calculates quantity for quantity packs, handling unit conversions and incremental additions
 */
export const calculateQuantityPackQuantity = (baseQuantity, isIncremental, includedWithQuantityPack, ownedQuantity) => {
  const isSmallUnitQuantity = baseQuantity < includedWithQuantityPack;
  if (isIncremental) {
    // Incremental quantities are always in small units, convert to large units
    const requestedQuantityInLargeUnits = baseQuantity * includedWithQuantityPack;
    return ownedQuantity + requestedQuantityInLargeUnits;
  }

  // Convert small units to large units for display (e.g., 10 contacts -> 10000)
  return isSmallUnitQuantity ? baseQuantity * includedWithQuantityPack : baseQuantity;
};